import React, { useEffect, useRef, useState } from "react";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import FormField from "../../components/Form/FormField";
import FormTextArea from "../../components/Form/FormTextArea";
import { Spin, Switch } from "antd";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../app/store";
import {
  addNewUser,
  fetchUserMe,
  getUserById,
  updateUserById,
} from "../../app/userSlice";
import { fetchUserDropdownList } from "../../services/authServices";
import { fetchRoleDropdownList } from "../../services/roleServices";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DeleteOutlined } from "@ant-design/icons";
import { Eye, EyeOff } from "lucide-react";
import './CreateTeam.scss';

interface User {
  id: number;
  name?: string;
}
interface Role {
  id: number;
  name?: string;
}

const CoreTeamCreate = () => {
  const location = useLocation();
  const { id } = location.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const initialFormData = {
    firstName: "",
    lastName: "",
    photo: null as File | null,
    designation: "",
    mobile: "",
    email: "",
    address: "",
    emergencyContact: "",
    reportingManager: "",
    hasReportingManager: false,
    isCoreTeamMember: false,
    thirdPartyName: "",
    status: "active",
    dob: "",
    gender: "",
    role_id: "",
    unique_code: "",
    shoreType: "shore",
    password: "",
    confirmPassword: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isStaff =
    roles
      .find((r) => r.id.toString() === formData.role_id)
      ?.name?.toLowerCase() !== "staff";

  // 🧠 Utility to map user response -> form state
  const mapUserToForm = (user: any) => ({
    firstName: user.first_name || "",
    lastName: user.last_name || "",
    photo: user.profile_image || "",
    status: user.status || "active",
    designation: user.designation || "",
    mobile: `${user.country_code}${user.mobile}`,
    email: user.email || "",
    address: user.address || "",
    emergencyContact: `${user.emergency_contact_country_code}${user.emergency_contact}`,
    reportingManager: user.reporting_manager_id?.toString() || "",
    hasReportingManager: Boolean(user.reporting_manager_id),
    isCoreTeamMember: user.core_member === 0,
    thirdPartyName: user.third_party_name || "",
    dob: user.dob || "",
    gender: user.gender || "",
    role_id: user.role_id?.toString() || "",
    unique_code: user.unique_code || "",
    shoreType:
      user.is_shor === "yes"
        ? "shore"
        : user.is_shor === "no"
        ? "offshore"
        : "shoreoffshore",
    password: user.password || "",
    confirmPassword: user.password || "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [roleResult, userResult] = await Promise.all([
          fetchRoleDropdownList(),
          fetchUserDropdownList(),
        ]);
        setRoles(roleResult);
        setUsers(userResult);
      } catch (err: any) {
        toast.error(err.message || "Failed to load dropdown data");
      }
    })();
  }, []);
  useEffect(() => {
    if (!id) {
      setFormData(initialFormData);
      setPreview(null);
      setErrors({});
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    (async () => {
      setUserLoading(true);
      try {
        const res = await dispatch(getUserById(Number(id))).unwrap();
        const userData = res?.data;
        if (userData) {
          setFormData(mapUserToForm(userData));
          setPreview(userData.profile_image || "");
        }
      } catch {
        toast.error("Failed to fetch user details");
      } finally {
        setUserLoading(false);
      }
    })();
  }, [id, dispatch]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, files } = e.target as HTMLInputElement;

    // 📷 Handle file input (profile image)
    if (type === "file") {
      const file = files?.[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      const maxSize = 5 * 1024 * 1024;

      if (!file) {
        setFormData((prev) => ({ ...prev, [name]: null }));
        setPreview(null);
        return;
      }

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: "Only JPG, JPEG, PNG allowed",
        }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({ ...prev, [name]: "Max file size is 5MB" }));
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: file }));
      setPreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    // 📝 Handle text/select/textarea input
    setFormData((prev) => ({ ...prev, [name]: value.trimStart() }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (value: string | undefined, name: string) => {
    setFormData((prev) => ({ ...prev, [name]: value ?? "" }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      hasReportingManager: checked,
      reportingManager: checked ? prev.reportingManager : "",
    }));
    setErrors((prev) => ({ ...prev, reportingManager: "" }));
  };

  const handleCoreTeamToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isCoreTeamMember: checked,
      thirdPartyName: checked ? prev.thirdPartyName : "",
    }));
    setErrors((prev) => ({ ...prev, thirdPartyName: "" }));
  };

  const handlePasswordToggle = (checked: boolean) => {
    setChangePassword(checked);

    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        password: "",
        confirmPassword: "",
      }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const requiredFields: [keyof typeof formData, string][] = [
      ["role_id", "Please select role name"],
      ["unique_code", "Unique Code is required"],
      ["firstName", "First name is required"],
      ["lastName", "Last name is required"],
      ["email", "Email is required"],
    ];

    requiredFields.forEach(([field, message]) => {
      if (!formData[field]?.toString().trim()) newErrors[field] = message;
    });

    if (formData.isCoreTeamMember && !formData.thirdPartyName.trim() && !isStaff) {
      newErrors.thirdPartyName = "Third Party Name is required";
    }

    if (formData.hasReportingManager && !formData.reportingManager && !isStaff) {
      newErrors.reportingManager = "Select a reporting manager";
    }
    if (formData.hasReportingManager === false && !isStaff) {
      newErrors.hasReportingManager = "Please assign a reporting manager";
    }
    const mustRequirePassword = changePassword; // Create OR Update+Toggle
    // Password required check
    if (mustRequirePassword && !formData.password?.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password) {
      if (formData.password.length < 8 || formData.password.length > 16) {
        newErrors.password = "Password must be 8 to 16 characters long";
      }
      if (!/[A-Z]/.test(formData.password)) {
        newErrors.password =
          "Password must include at least one uppercase letter";
      }
      if (!/[a-z]/.test(formData.password)) {
        newErrors.password =
          "Password must include at least one lowercase letter";
      }
      if (!/[0-9]/.test(formData.password)) {
        newErrors.password = "Password must include at least one digit";
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
        newErrors.password =
          "Password must include at least one special character";
      }

      // Confirm password match
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Confirm password does not match password";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setBtnLoading(true);
    const data = new FormData();
    if (formData.role_id) data.append("role_id", formData.role_id);
    if (formData.status) data.append("status", formData.status);
    if (formData.unique_code?.trim())
      data.append("unique_code", formData.unique_code);
    if (formData.firstName?.trim())
      data.append("first_name", formData.firstName);
    if (formData.lastName?.trim()) data.append("last_name", formData.lastName);
    if (formData.email?.trim()) data.append("email", formData.email);
    if (formData.gender) data.append("gender", formData.gender);
    if (formData.dob) data.append("dob", formData.dob);
    if (formData.designation?.trim())
      data.append("designation", formData.designation);
    if (formData.address?.trim()) data.append("address", formData.address);
    data.append("country_code", "+91");
    data.append("country_iso", "IN");
    if (!id) {
      // create case → always send password
      data.append("password", formData.password);
    } else if (changePassword) {
      // update case → only when toggle checked
      data.append("change_password", "1");
      data.append("password", formData.password);
    }
    if (formData.mobile?.startsWith("+91")) {
      const mobileOnly = formData.mobile.replace("+91", "").trim();
      data.append("mobile", mobileOnly);
    } else {
      data.append("mobile", "");
    }
    // Shore logic
    const isShorValue =
      formData.shoreType === "shore"
        ? "yes"
        : formData.shoreType === "offshore"
        ? "no"
        : "both";

    data.append("is_shor", isShorValue);

    // Third party name if core team member
    if (formData.isCoreTeamMember && formData.thirdPartyName?.trim()) {
      data.append("third_party_name", formData.thirdPartyName);
    }

    // Staff-specific fields
    if (!isStaff) {
      data.append("core_member", formData.isCoreTeamMember ? "0" : "1");
      data.append("emergency_contact_country_code", "+91");
      if (formData.emergencyContact?.startsWith("+91")) {
        const mobileOnly = formData.emergencyContact.replace("+91", "").trim();
        data.append("emergency_contact", mobileOnly);
      } else {
        data.append("emergency_contact", "");
      }
      data.append("is_team_member", formData.hasReportingManager ? "1" : "0");
      if (formData.hasReportingManager && formData.reportingManager) {
        data.append("reporting_manager_id", formData.reportingManager);
      }
    }
    if (formData.photo instanceof File && formData.photo.size > 0) {
      data.append("profile_image", formData.photo);
    } else if (typeof formData.photo === "string" && formData.photo !== "") {
      const fileFromUrl = await urlToFile(formData.photo);
      if (fileFromUrl) {
        data.append("profile_image", fileFromUrl);
      }
    } else {
      data.append("profile_image", "");
    }

    try {
      const result = id
        ? await dispatch(updateUserById({ id: Number(id), formData: data }))
        : await dispatch(addNewUser(data));

      if (
        addNewUser.fulfilled.match(result) ||
        updateUserById.fulfilled.match(result)
      ) {
        toast.success(`User ${id ? "updated" : "created"} successfully!`);
        const updatedUser = await dispatch(fetchUserMe()).unwrap();
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.dispatchEvent(new Event("storage"));
        navigate("/dashboard/userListing");
      } else {
        toast.error(
          (result.payload as string) ||
            `User ${id ? "update" : "create"} failed`
        );
      }
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setBtnLoading(false);
    }
  };
  const urlToFile = async (url: string, mimeType = "image/jpeg") => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch image");

      const blob = await response.blob();
      const filename = url.split("/").pop() || "image.jpg";
      return new File([blob], filename, { type: mimeType });
    } catch (err) {
      return null;
    }
  };

  const openDatePicker = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker?.(); // for modern browsers
      dateInputRef.current.focus(); // fallback
    }
  };
  return (
    <>
      <Breadcrumb pageName={id ? "Update User" : "Create User"} />
      {userLoading ? (
        <div className="flex justify-center items-center h-96">
          {/* <span className="h-10 w-10 border-4 border-gray-300 border-t-black rounded-full animate-spin"></span> */}
          <Spin size="large" />
        </div>
      ) : (
        <div className="bg-white dark:bg-boxdark p-6 rounded-md shadow-md max-w-7xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* First row: Select Role + Unique Code */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                  Select Role <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    className={`appearance-none w-full rounded-lg border py-2 px-4 pr-10 dark:bg-form-input dark:text-white ${
                      errors.role_id ? "border-red-500" : "border-stroke"
                    }`}
                  >
                    <option value="" disabled hidden>-- Select Role --</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id.toString()}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                {errors.role_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.role_id}</p>
                )}
              </div>

              <FormField
                label="Unique Code"
                name="unique_code"
                value={formData.unique_code}
                onChange={handleChange}
                error={errors.unique_code}
                required
                maxLength={20}
              />
            </div>
            <div className="relative">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Status
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                    errors.status
                      ? "border-red-500"
                      : "border-stroke dark:border-form-strokedark"
                  }`}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  {/* <option value="completed">Completed</option> */}
                </select>
                <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              {errors.status && (
                <p className="text-red-500 text-sm mt-1">{errors.status}</p>
              )}
            </div>
            <FormField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={errors.firstName}
              required
              maxLength={50}
            />
            <FormField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={errors.lastName}
              required
              maxLength={50}
            />
            {!isStaff && (
              <FormField
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                error={errors.designation}
                maxLength={25}
              />
            )}
            <FormField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
              maxLength={100}
            />
            {/* Gender */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Gender
              </label>
              <div className="flex items-center gap-6">
                {["Male", "Female", "Prefer not to say"].map((option) => {
                  const value =
                    option === "Prefer not to say"
                      ? "other"
                      : option.toLowerCase();

                  return (
                    <label
                      key={option}
                      className="flex items-center gap-2 text-gray-700 dark:text-white"
                    >
                      <input
                        type="radio"
                        name="gender"
                        value={value}
                        checked={formData.gender === value}
                        onChange={handleChange}
                        className="text-black dark:text-white"
                      />
                      {option}
                    </label>
                  );
                })}
              </div>
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
              )}
            </div>
            {/* DOB */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Date of Birth
              </label>

              <div
                onClick={openDatePicker}
                className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white cursor-pointer ${
                  errors.dob ? "border-red-500" : "border-stroke"
                }`}
              >
                <input
                  ref={dateInputRef}
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full bg-transparent outline-none cursor-pointer"
                />
              </div>

              {errors.dob && (
                <p className="text-red-500 text-sm mt-1">{errors.dob}</p>
              )}
            </div>

            {/* Mobile */}
            <div className="relative z-50">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Mobile Number
              </label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={formData.mobile}
                onChange={(value) => handlePhoneChange(value, "mobile")}
                countryCallingCodeEditable={false}
                placeholder="Enter phone number"
                onKeyDownCapture={(
                  e: React.KeyboardEvent<HTMLInputElement>
                ) => {
                  const key = e.key;
                  const isDigit = /^[0-9]$/.test(key);
                  const allowedKeys = [
                    "Backspace",
                    "ArrowLeft",
                    "ArrowRight",
                    "Delete",
                    "Tab",
                  ];
                  const digitsOnly = formData.mobile.replace(/\D/g, "");

                  if (!isDigit && !allowedKeys.includes(key)) {
                    e.preventDefault();
                  }

                  if (isDigit && digitsOnly.length >= 12) {
                    e.preventDefault();
                  }
                }}
                countrySelectProps={{
                  style: { display: "none" }, // ✅ hides the dropdown
                }}
                className={`!w-full !rounded-md !border !bg-transparent !px-4 !text-black dark:!border-gray-300 dark:!bg-form-input dark:!text-black ${
                  errors.mobile ? "border-red-500" : "border-stroke"
                }`}
              />
              {errors.mobile && (
                <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>
              )}
            </div>
            {/* Emergency Contact */}
            {!isStaff && (
              <div className="relative z-50">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                  Emergency Contact
                </label>
                <PhoneInput
                  defaultCountry="IN"
                  international
                  countryCallingCodeEditable={false}
                  value={formData.emergencyContact}
                  onChange={(value) =>
                    handlePhoneChange(value, "emergencyContact")
                  }
                  placeholder="Enter emergency number"
                  onKeyDownCapture={(
                    e: React.KeyboardEvent<HTMLInputElement>
                  ) => {
                    const key = e.key;
                    const isDigit = /^[0-9]$/.test(key);
                    const allowedKeys = [
                      "Backspace",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                      "Tab",
                    ];
                    const digitsOnly = formData.emergencyContact.replace(
                      /\D/g,
                      ""
                    );

                    if (!isDigit && !allowedKeys.includes(key)) {
                      e.preventDefault();
                    }

                    if (isDigit && digitsOnly.length >= 12) {
                      e.preventDefault();
                    }
                  }}
                  countrySelectProps={{
                    style: { display: "none" }, // ✅ hides the dropdown
                  }}
                  className={`!w-full !rounded-md !border !bg-transparent !px-4 !text-black dark:!border-gray-300 dark:!bg-form-input dark:!text-black ${
                    errors.emergencyContact ? "border-red-500" : "border-stroke"
                  }`}
                />
                {errors.emergencyContact && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.emergencyContact}
                  </p>
                )}
              </div>
            )}
            {/* Address */}
            {!isStaff && (
              <FormTextArea
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                error={errors.address}
              />
            )}
            {/* Profile Picture */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Profile Picture
              </label>
              <div
                className={`flex items-center w-full rounded-lg border px-4 py-2 bg-gray-100 dark:bg-form-input dark:text-white ${
                  errors.image
                    ? "border-red-500"
                    : "border-stroke dark:border-form-strokedark"
                }`}
                onClick={() =>
                  document.getElementById("profile-image")?.click()
                }
              >
                <input
                  id="profile-image"
                  type="file"
                  name="photo"
                  accept="image/*"
                  onChange={handleChange}
                  className="hidden"
                />
                {/* Choose Label */}
                <span className="text-sm text-gray-800 dark:text-gray-300">
                  Choose File
                </span>
                {/* File name and Remove icon */}
                <div className="ml-auto flex items-center gap-2 truncate">
                  <span className="text-xs  text-gray-600 dark:text-gray-300 truncate max-w-[150px]">
                    {preview
                      ? formData.photo instanceof File
                        ? formData.photo.name
                        : preview.split("/").pop()
                      : "No file chosen"}
                  </span>
                  {/* Remove Icon (only if preview exists) */}
                  {preview && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData((prev) => ({ ...prev, photo: null }));
                        setPreview(null);
                      }}
                      className="text-red-500 hover:text-red-700"
                      title="Remove selected image"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              </div>
              {/* Image Preview */}
              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="mt-2 h-20 w-20 object-cover rounded-full"
                />
              )}
              {errors.photo && (
                <p className="text-red-500 text-sm mt-1">{errors.photo}</p>
              )}
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Shore Type
              </label>
              <div className="relative">
                <select
                  name="shoreType"
                  value={formData.shoreType}
                  onChange={handleChange}
                  className="appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white border-stroke"
                >
                  <option value="shore">Shore</option>
                  <option value="offshore">Offshore</option>
                  <option value="shoreoffshore">Shore/Offshore</option>
                </select>
                <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
            {id && (
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-medium text-gray-700 dark:text-white">
                    Would You Like To Change Password?
                  </span>
                  <Switch
                    checked={changePassword}
                    onChange={handlePasswordToggle}
                  />
                </div>
              </div>
            )}

            {(!id || changePassword) && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Password */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      // minLength={8}
                      // maxLength={16}
                      className="w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white border-stroke"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400"
                    >
                      <span className="absolute right-2 top-2.5">
                        {!showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </span>
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>
                {/* Confirm Password */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white border-stroke"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400"
                    >
                      <span className="absolute right-2 top-2.5">
                        {!showConfirmPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </span>
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            )}
            {!isStaff && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Reporting Manager Toggle */}
                <div className="md:col-span-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-medium text-gray-700 dark:text-white">
                      Has Reporting Manager?
                    </span>
                    <Switch
                      checked={formData.hasReportingManager}
                      onChange={handleSwitchChange}
                    />
                    {errors.hasReportingManager && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.hasReportingManager}
                      </p>
                    )}
                  </div>

                  {/* Conditionally show dropdown only if checked */}
                  {formData.hasReportingManager && (
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                        Reporting Manager
                      </label>
                      <div className="relative">
                        <select
                          name="reportingManager"
                          value={formData.reportingManager}
                          onChange={handleChange}
                          className={`appearance-none w-full rounded-lg border py-2 px-4  pr-10 dark:bg-form-input dark:text-white ${
                            errors.reportingManager
                              ? "border-red-500"
                              : "border-stroke"
                          }`}
                        >
                          <option value="" disabled hidden>Select Manager</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      {errors.reportingManager && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.reportingManager}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Core Team Member Toggle */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-medium text-gray-700 dark:text-white">
                      Is Third Party Member?
                    </span>
                    <Switch
                      checked={formData.isCoreTeamMember}
                      onChange={handleCoreTeamToggle}
                    />
                  </div>

                  {formData.isCoreTeamMember && (
                    <FormField
                      label="Third Party Name"
                      name="thirdPartyName"
                      value={formData.thirdPartyName}
                      onChange={handleChange}
                      error={errors.thirdPartyName}
                      maxLength={30}
                    />
                  )}
                </div>
              </div>
            )}
            {/* Submit */}
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={btnLoading}
                className={`w-full bg-black text-white py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                  btnLoading
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:bg-opacity-90"
                }`}
              >
                {id ? "Update" : "Save"}
                {btnLoading && (
                  <span className="h-4 w-4 ml-2 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default CoreTeamCreate;
